import React, { useEffect } from "react";
import { useField } from "formik";
import { Form } from "react-bootstrap";
import { SntSelectWidget, SntMultiSelectWidget, type SntSelectFilter, type SntSelectOption } from "@cleanup/widgets/selectors/SntSelect";

/** A choice of the item selector. The formik value holds these objects (one, or an array when `multi`). */
interface Option {
  value: string | number | boolean;
  label: string | number;
}

interface ItemSelectorProps {
  name: string;
  multi?: boolean;
  placeholder?: string;
  disabled?: boolean;
  options?: readonly Option[];
  handleChange?: (value: Option | Option[] | null) => void;
  isClearable?: boolean;
  filterOption?: SntSelectFilter;
}

// the select takes string or number values: a boolean value becomes "true"/"false";
// the caller's options are looked up back by value
const toSelectOption = (option: Option): SntSelectOption => ({
  value: typeof option.value === "boolean" ? String(option.value) : option.value,
  label: String(option.label),
});

const ItemSelector: React.FC<ItemSelectorProps> = ({
  name,
  multi = false,
  placeholder = "",
  disabled = false,
  options = [],
  handleChange,
  isClearable,
  filterOption = undefined,
}) => {
  const [field, meta, helper] = useField<Option | Option[] | null>(name);

  useEffect(() => {
    handleChange?.(field.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  let formGroupClass = "";
  if (meta && meta.error) {
    formGroupClass = "is-invalid";
  } else {
    formGroupClass = "is-valid";
  }

  useEffect(() => {
    const current = Array.isArray(field.value) ? null : field.value;
    const initial: Option | Option[] | null = meta.initialValue ?? null;
    if (
      current &&
      !options.find((ops) => ops.value === current.value) &&
      options[0] &&
      !multi
    ) {
      helper.setValue(
        options.find((ops) => !Array.isArray(initial) && ops.value === initial?.value) ||
          options[0]
      );
    }
  }, [options, field.value, meta.initialValue, multi, helper]);

  const fieldValues = (): Option[] =>
    Array.isArray(field.value) ? field.value : field.value ? [field.value] : [];

  // the selected options, or (while there are no options yet) the current form values
  const getValues = (): SntSelectOption[] => {
    if (options.length > 0) {
      return options
        .filter((ops) => fieldValues().some((f) => f.value === ops.value))
        .map(toSelectOption);
    }
    return fieldValues().map(toSelectOption);
  };

  const selectOptions = options.map(toSelectOption);

  // the caller's own option object behind a select option
  const toCallerOption = (option: SntSelectOption): Option =>
    options.find((ops) => String(ops.value) === String(option.value)) ??
    fieldValues().find((f) => String(f.value) === String(option.value)) ??
    option;

  return (
    <>
      {multi ? (
        <SntMultiSelectWidget
          className={`${formGroupClass}`}
          name={name}
          placeholder={placeholder}
          options={selectOptions}
          disabled={disabled}
          value={getValues()}
          onChange={(selected: SntSelectOption[]) => helper.setValue(selected.map(toCallerOption))}
          isClearable={isClearable}
          filterOption={filterOption}
        />
      ) : (
        <SntSelectWidget
          className={`${formGroupClass}`}
          name={name}
          placeholder={placeholder}
          options={selectOptions}
          disabled={disabled}
          value={getValues()[0] ?? null}
          onChange={(option: SntSelectOption | null) =>
            helper.setValue(option ? toCallerOption(option) : null)
          }
          isClearable={isClearable}
          filterOption={filterOption}
        />
      )}
      <Form.Control.Feedback type="invalid">{meta.error}</Form.Control.Feedback>
    </>
  );
};

export default ItemSelector;
export type { Option };
